import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Sun, Moon, Eye, EyeOff, ShieldAlert } from 'lucide-react';
import { SIGNED_OUT_FLAG, SCREENSHOT_SIGN_OUT } from '../components/focus-guard/FocusGuardProvider';

const Login = () => {
    const navigate = useNavigate();
    const { login } = useAuth();
    const { theme, toggleTheme } = useTheme();
    
    const [loginData, setLoginData] = useState({ email: '', password: '' });
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    // Flag is cleared on the next successful login (not on mount): the strike-out flow mounts Login twice (router redirect, then a full reload)
    const [signedOutLimit] = useState(() => sessionStorage.getItem(SIGNED_OUT_FLAG));

    const handleLogin = async (e) => {
        e.preventDefault();
        setLoading(true);

        const result = await login(loginData.email, loginData.password);
        
        if (result.success) {
            sessionStorage.removeItem(SIGNED_OUT_FLAG);
            toast.success('Welcome back!', { description: `Logged in as ${result.user.name}` });
            navigate('/dashboard');
        } else {
            toast.error('Login failed', { description: result.error });
        }
        
        setLoading(false);
    };

    return (
        <div className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
            {/* Animated Background Elements */}
            <div className="absolute inset-0 overflow-hidden">
                {/* Grid Pattern */}
                <div 
                    className="absolute inset-0 opacity-[0.04]"
                    style={{
                        backgroundImage: 'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)',
                        backgroundSize: '50px 50px'
                    }}
                />
                {/* Glowing Orbs - Cyan themed */}
                <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-cyan-500/15 rounded-full blur-[120px] animate-pulse" />
                <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-cyan-400/10 rounded-full blur-[120px] animate-pulse" style={{ animationDelay: '1s' }} />
                <div className="absolute top-1/2 right-1/3 w-64 h-64 bg-cyan-500/10 rounded-full blur-[100px] animate-pulse" style={{ animationDelay: '2s' }} />
            </div>

            {/* Theme Toggle */}
            <Button
                variant="ghost"
                size="icon"
                onClick={toggleTheme}
                className="absolute top-4 right-4 z-10 text-white/70 hover:text-white"
                data-testid="login-theme-toggle"
            >
                {theme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </Button>

            <div className="w-full max-w-md relative z-10 animate-fade-in">
                {/* Logo & Branding */}
                <div className="flex flex-col items-center mb-8">
                    {/* Logo Container with Light Background */}
                    <div className="relative mb-4 p-6 rounded-2xl bg-gradient-to-br from-white/95 via-gray-50/90 to-white/85 shadow-2xl shadow-cyan-500/20">
                        {/* Subtle glow effect */}
                        <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-cyan-400/10 to-transparent" />
                        <img 
                            src="/tekwissen-logo.png" 
                            alt="TekWissen" 
                            className="relative h-20 w-auto object-contain"
                            style={{ maxWidth: '260px' }}
                        />
                    </div>
                    
                    {/* Smart Trainer Subtitle */}
                    <h1 className="font-heading text-2xl font-bold tracking-tight text-white mt-2">
                        Smart Trainer
                    </h1>
                    <p className="text-cyan-400/90 text-sm mt-1">
                        AI-Powered Training Platform
                    </p>
                </div>

                {/* Login Card */}
                <Card className="border-white/10 bg-white/5 backdrop-blur-xl shadow-xl">
                    <form onSubmit={handleLogin}>
                        <CardHeader className="px-6 pt-6 pb-4">
                            <CardTitle className="text-xl text-white">Welcome back</CardTitle>
                            <CardDescription className="text-white/60">
                                Enter your credentials to continue
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4 px-6 pb-6">
                            {signedOutLimit && (
                                <div className="flex items-start gap-2 rounded-lg border border-red-400/40 bg-red-500/15 px-3 py-2.5 text-sm text-red-100" data-testid="focus-guard-signed-out-notice">
                                    <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" />
                                    <span>{signedOutLimit === SCREENSHOT_SIGN_OUT
                                        ? 'You were signed out because a screenshot was attempted. Screenshots are not allowed during training.'
                                        : `You were signed out because you left the training page ${signedOutLimit} ${signedOutLimit === '1' ? 'time' : 'times'}. Please stay on the page while you train.`}</span>
                                </div>
                            )}
                            <div className="space-y-2">
                                <Label htmlFor="login-email" className="text-white/80">Email</Label>
                                <Input
                                    id="login-email"
                                    type="email"
                                    placeholder="you@example.com"
                                    value={loginData.email}
                                    onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                                    required
                                    data-testid="login-email"
                                    className="bg-white/10 border-white/20 text-white placeholder:text-white/40 focus:border-cyan-500 focus:ring-cyan-500/20"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="login-password" className="text-white/80">Password</Label>
                                <div className="relative">
                                    <Input
                                        id="login-password"
                                        type={showPassword ? 'text' : 'password'}
                                        placeholder="••••••••"
                                        value={loginData.password}
                                        onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                                        required
                                        data-testid="login-password"
                                        className="bg-white/10 border-white/20 text-white placeholder:text-white/40 focus:border-cyan-500 focus:ring-cyan-500/20 pr-10"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-white/50 hover:text-white/80 transition-colors"
                                    >
                                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                    </button>
                                </div>
                            </div>
                            <Button 
                                type="submit" 
                                className="w-full bg-cyan-600 hover:bg-cyan-700 text-white font-medium mt-2 shadow-lg shadow-cyan-600/30 transition-all hover:shadow-cyan-600/40" 
                                disabled={loading}
                                data-testid="login-submit"
                            >
                                {loading ? 'Signing in...' : 'Sign In'}
                            </Button>
                        </CardContent>
                    </form>
                </Card>
                
                {/* Footer Text */}
                <p className="text-center text-white/40 text-xs mt-6">
                    Empower Your Business with Intelligent Learning
                </p>
            </div>
        </div>
    );
};

export default Login;
